import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { getApiProfileMeOptions } from 'api/generated/@tanstack/react-query.gen';
import { useAppState } from 'lib/app-state';
import { isUnadjudicated } from 'lib/auth/session-error';
import { retryUnlessClientError } from 'lib/helpers/query-retry';

/**
 * `unknown` is the important one: it means "we can't say yet", and is what the
 * UI must show a loading state for. Treating it as signed-out is what made a
 * slow or flaky `/api/profile/me` look like an expired login.
 */
export type AuthStatus =
  /** Still asking. Render a loading state, not a sign-in form. */
  | 'unknown'
  | 'authenticated'
  | 'unauthenticated'
  /** We asked, we couldn't find out, and we've stopped trying. The tokens are
   *  still there and may still be good — this is not a sign-out. */
  | 'unavailable';

const statusOf = (error: unknown) =>
  (error as AxiosError | undefined)?.response?.status;

/**
 * The single answer to "is this customer signed in?".
 *
 * Backed by the tokens in storage plus `/api/profile/me`, which every
 * authenticated page already fetches — React Query dedupes on the shared key,
 * so using this hook adds no request.
 */
export function useAuthSession() {
  const { tokens } = useAppState();
  const hasTokens = !!tokens?.accessToken;

  const meQuery = useQuery({
    ...getApiProfileMeOptions(),
    enabled: hasTokens,
    // A 401 here has already survived the interceptor's refresh attempt, so
    // retrying it only delays the sign-out — unless the interceptor marked it
    // as never adjudicated, which the shared predicate knows about. Server and
    // network errors still get three attempts.
    retry: retryUnlessClientError,
    // Coming back to a tab left open for hours is exactly when a session has
    // silently died. Enabled for this query alone — the global default stays
    // off so seat and event queries don't re-storm on every tab switch.
    refetchOnWindowFocus: true,
  });

  const status: AuthStatus = (() => {
    if (!hasTokens) return 'unauthenticated';
    if (meQuery.isSuccess) return 'authenticated';
    // React Query only flips `isError` once the retry predicate has given up,
    // so reaching here means we are done trying, not still trying.
    if (meQuery.isError) {
      const code = statusOf(meQuery.error);
      // Only the server saying "no" signs the customer out — and only when it
      // actually got to say it. A 401 the refresh could never adjudicate, a
      // 5xx or a dropped connection all mean we don't know.
      if ((code === 401 || code === 403) && !isUnadjudicated(meQuery.error)) {
        return 'unauthenticated';
      }
      return 'unavailable';
    }
    return 'unknown';
  })();

  return {
    status,
    isAuthenticated: status === 'authenticated',
    /** True while the answer is still unknown — render a loading state, not a sign-in form. */
    isResolving: status === 'unknown',
    /** Terminal: render an error with a retry, not a spinner and not a sign-in form. */
    isUnavailable: status === 'unavailable',
    /** The error behind `unavailable`, ready for `<GenericError error={...} />`. */
    error: status === 'unavailable' ? meQuery.error : undefined,
    /** Ask again. Wire this to the retry affordance. */
    retry: meQuery.refetch,
    profile: meQuery.data,
    meQuery,
  };
}
